import type { AgentDiscoveryResult } from './types.js';
import { AgentDiscoveryError } from './errors.js';
import { resolveFetchImpl } from './fetch.js';
import { toWsLoginUrl } from './ws-envelope.js';

const DEFAULT_AGENT_PORT = 57601;
/** Canonical Agent hostname (public DNS → 127.0.0.1). No IP discovery fallback. */
export const AGENT_HOST = 'localsign.ksign.tr';

/** Full order (HTTP pages / Node). HTTPS pages use a secure subset. */
export const DISCOVERY_PING_PATHS = [
  { scheme: 'https', host: AGENT_HOST },
  { scheme: 'http', host: AGENT_HOST },
] as const;

/** HTTPS page: only HTTPS candidates (mixed content otherwise). */
export const DISCOVERY_PING_PATHS_SECURE = [
  { scheme: 'https', host: AGENT_HOST },
] as const;

export type TargetAddressSpace = 'loopback' | 'local' | 'public';

/** Fetch init for Agent on loopback — required for Chrome Local Network Access (LNA). */
export function agentFetchInit(init: RequestInit = {}): RequestInit {
  return {
    ...init,
    // Chrome 142+: public site → localsign.ksign.tr (loopback via DNS) needs explicit loopback intent.
    // Browsers that ignore unknown options still work.
    ...({ targetAddressSpace: 'loopback' as TargetAddressSpace } as RequestInit),
  };
}

export function isSecureBrowserContext(): boolean {
  if (typeof globalThis === 'undefined') {
    return false;
  }
  const loc = (globalThis as { location?: { protocol?: string } }).location;
  return loc?.protocol === 'https:';
}

export function discoveryCandidates(
  secureContext: boolean = isSecureBrowserContext(),
): readonly { scheme: string; host: string }[] {
  return secureContext ? DISCOVERY_PING_PATHS_SECURE : DISCOVERY_PING_PATHS;
}

export async function discoverAgent(
  port: number = DEFAULT_AGENT_PORT,
  fetchImpl: typeof fetch = resolveFetchImpl(),
  timeoutMs = 3000,
): Promise<AgentDiscoveryResult> {
  const boundFetch = resolveFetchImpl(fetchImpl);
  const candidates = discoveryCandidates();
  for (const candidate of candidates) {
    const pingUrl = `${candidate.scheme}://${candidate.host}:${port}/ping`;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await boundFetch(
        pingUrl,
        agentFetchInit({
          method: 'GET',
          signal: controller.signal,
        }),
      );
      if (!response.ok) {
        continue;
      }
      const body = (await response.json()) as { success?: boolean; status?: string };
      if (body.success !== true || body.status !== 'running') {
        continue;
      }
      const agentBaseUrl = `${candidate.scheme}://${candidate.host}:${port}`;
      return {
        pingUrl,
        agentBaseUrl,
        wsLoginUrl: toWsLoginUrl(agentBaseUrl),
        agentApiBase: `${agentBaseUrl}/api/v1`,
      };
    } catch {
      // try next candidate
    } finally {
      clearTimeout(timer);
    }
  }
  throw new AgentDiscoveryError();
}

export function resolveOrigin(configOrigin?: string): string {
  if (configOrigin) {
    return configOrigin;
  }
  if (typeof globalThis !== 'undefined' && 'location' in globalThis) {
    const loc = (globalThis as { location?: { origin?: string } }).location;
    if (loc?.origin) {
      return loc.origin;
    }
  }
  throw new Error('origin is required when not running in a browser');
}

export function resolveWebSocketImpl(
  custom?: new (url: string, protocols?: string | string[]) => import('./types.js').WebSocketLike,
): new (url: string, protocols?: string | string[]) => import('./types.js').WebSocketLike {
  if (custom) {
    return custom;
  }
  const impl = (globalThis as { WebSocket?: typeof WebSocket }).WebSocket;
  if (!impl) {
    throw new Error('WebSocket is not available in this environment');
  }
  return impl as unknown as new (
    url: string,
    protocols?: string | string[],
  ) => import('./types.js').WebSocketLike;
}
